import { inject, injectable } from 'inversify';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import type JsonRpcDispatcher from '@dealroadshow/json-rpc-dispatcher';
import Request from '@framework/api/Rpc/Request';
import AskAiDITypes from '@creditflowresearch/socket-client/socket-ask-ai-client/DITypes';
import {
  AskAiProduct,
  IAskAiChatListResult,
  IAskAiChatMessagesResult,
  IAskAiExportResult,
} from '@/creditflowresearch/domain/vo/AskAiChat';

const hasError = (result: unknown): result is { error: string } => (
  typeof result === 'object' && result !== null && 'error' in result && typeof result.error === 'string'
);

const throwOnError = (result: unknown): void => {
  if (hasError(result)) {
    throw new Error(result.error);
  }
};

@injectable()
class AskAiChatRepository {
  constructor(@inject(AskAiDITypes.JsonRpcDispatcher) private readonly rpc: JsonRpcDispatcher) {}

  getChats = async (page: number, perPage: number): Promise<IAskAiChatListResult> => {
    const request = new Request('cfr.ask_ai.history', { page, perPage });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  };

  getMessages = async (
    chatId: string,
    perPage: number,
    before?: string | null,
  ): Promise<IAskAiChatMessagesResult> => {
    const request = new Request('cfr.ask_ai.chat_messages', {
      chatId,
      perPage,
      ...(before ? { before } : {}),
    });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  };

  deleteChat = async (chatId: string): Promise<void> => {
    const request = new Request('cfr.ask_ai.delete_chat', { chatId });
    const response = await this.rpc.call<RpcSuccess>(request);

    throwOnError(response.getResult());
  };

  export = async (
    chatId: string,
    messageId: string,
    product?: AskAiProduct,
  ): Promise<IAskAiExportResult> => {
    const request = new Request('cfr.ask_ai.export', {
      chatId,
      messageId,
      ...(product ? { product } : {}),
    });
    const response = await this.rpc.call<RpcSuccess>(request);
    const result = response.getResult();

    throwOnError(result);

    return result;
  };
}

export default AskAiChatRepository;
